import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics } from '@/mocks/comics';
import { useState } from 'react';

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16">
          <div className="w-full px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
              <i className="ri-emotion-sad-line text-3xl"></i>
            </div>
            <h1 className="mt-6 text-2xl font-bold text-foreground-800">漫画未找到</h1>
            <p className="mt-2 text-foreground-500">抱歉，没有找到该漫画，可能已被下架或链接有误。</p>
            <Link to="/" className="whitespace-nowrap mt-6 inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const sortedChapters = [...comic.chapters].sort((a, b) =>
    sortOrder === 'desc' ? b.number - a.number : a.number - b.number
  );

  const relatedComics = comics
    .filter((c) => c.id !== comic.id && c.categories.some((cat) => comic.categories.includes(cat)))
    .slice(0, 5);

  const catNameMap: Record<string, string> = {
    romance: '恋爱', fantasy: '奇幻', action: '动作', thriller: '悬疑',
    comedy: '搞笑', drama: '剧情', horror: '恐怖', daily: '日常',
  };

  const pageTitle = `${comic.title} - 韩国漫画网免费在线阅读`;
  const pageDesc = `${comic.title}是韩国漫画网推荐的${comic.categories.map((c) => catNameMap[c] || c).join('、')}题材韩漫，由${comic.author}创作。${comic.status === '连载中' ? '正在连载中' : '已完结'}，共${comic.chapters.length}话。无遮挡下拉式在线阅读，舒适流畅。`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'ComicStory',
    name: comic.title,
    author: { '@type': 'Person', name: comic.author },
    description: comic.description,
    genre: comic.categories.map((c) => catNameMap[c] || c),
    image: comic.coverUrl,
    inLanguage: 'zh-CN',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${comic.title}是免费的吗？`,
        acceptedAnswer: { '@type': 'Answer', text: `是的，${comic.title}在韩国漫画网完全免费阅读，无需付费订阅。` },
      },
      {
        '@type': 'Question',
        name: `${comic.title}的阅读体验如何？`,
        acceptedAnswer: { '@type': 'Answer', text: `韩国漫画网采用无遮挡下拉式阅读模式，${comic.title}的所有章节都支持流畅的下拉滚动阅读，无需翻页点击，让您专注于漫画情节。` },
      },
      {
        '@type': 'Question',
        name: `${comic.title}多久更新一次？`,
        acceptedAnswer: { '@type': 'Answer', text: `韩国漫画网保持与韩国同步更新速度，${comic.title}每周定期更新最新章节，敬请关注。` },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`${comic.title},${comic.categories.map((c) => catNameMap[c] || c).join(',')},韩国漫画,韩漫,免费漫画,在线阅读`}
      />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '漫画分类', path: '/category/all' },
              { label: catNameMap[comic.categories[0]] || comic.categories[0], path: `/category/${comic.categories[0]}` },
              { label: comic.title },
            ]}
          />
        </div>

        <section className="bg-background-50 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 py-8 md:py-14">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14">
              <div className="flex justify-center md:justify-start">
                <div className="w-64 md:w-80 rounded-2xl overflow-hidden bg-background-100 border border-background-200/70">
                  <img
                    src={comic.coverUrl}
                    alt={`${comic.title} - 韩国漫画网`}
                    title={`${comic.title} - ${comic.categories.map((c) => catNameMap[c] || c).join('、')}韩漫免费在线阅读`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    comic.status === '连载中'
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-secondary-500 text-background-50'
                  }`}>
                    {comic.status}
                  </span>
                  {comic.categories.map((cat) => (
                    <Link
                      key={cat}
                      to={`/category/${cat}`}
                      className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors"
                    >
                      {catNameMap[cat] || cat}
                    </Link>
                  ))}
                </div>

                <h1 className="font-heading text-3xl md:text-5xl font-black text-foreground-900 leading-tight">
                  {comic.title}
                </h1>

                <p className="mt-4 text-sm text-foreground-500 leading-relaxed">
                  {comic.description}
                </p>

                <div className="mt-6 flex flex-wrap items-center gap-6 text-sm">
                  <div>
                    <span className="text-foreground-400">作者</span>
                    <span className="ml-2 font-medium text-foreground-700">{comic.author}</span>
                  </div>
                  <div className="w-px h-5 bg-background-300"></div>
                  <div>
                    <span className="text-foreground-400">评分</span>
                    <span className="ml-2 font-medium text-accent-600">
                      <i className="ri-star-fill text-accent-500"></i> {comic.rating}
                    </span>
                  </div>
                  <div className="w-px h-5 bg-background-300"></div>
                  <div>
                    <span className="text-foreground-400">阅读量</span>
                    <span className="ml-2 font-medium text-foreground-700">
                      {comic.views > 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views}
                    </span>
                  </div>
                  <div className="w-px h-5 bg-background-300"></div>
                  <div>
                    <span className="text-foreground-400">总章节</span>
                    <span className="ml-2 font-medium text-foreground-700">{comic.chapters.length}话</span>
                  </div>
                </div>

                <div className="mt-8">
                  <Link
                    to="#"
                    className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-foreground-900 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors"
                  >
                    开始阅读
                    <i className="ri-arrow-right-up-line"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                章节列表
              </h2>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-400">排序：</span>
                <button
                  onClick={() => setSortOrder('desc')}
                  className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer ${
                    sortOrder === 'desc' ? 'bg-primary-500 text-background-50' : 'bg-background-50 text-foreground-600 border border-background-300/60'
                  }`}
                >
                  最新在前
                </button>
                <button
                  onClick={() => setSortOrder('asc')}
                  className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer ${
                    sortOrder === 'asc' ? 'bg-primary-500 text-background-50' : 'bg-background-50 text-foreground-600 border border-background-300/60'
                  }`}
                >
                  最早在前
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {sortedChapters.map((ch) => (
                <Link
                  key={ch.id}
                  to="#"
                  className="flex items-center justify-between p-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all group"
                >
                  <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-500 transition-colors truncate">
                    {ch.title}
                  </span>
                  <span className="text-xs text-foreground-400 flex-shrink-0 ml-2">
                    {ch.date}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="py-10 md:py-14 bg-background-50">
            <div className="w-full px-4 md:px-6">
              <div className="mb-8">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                  相关推荐
                </h2>
                <p className="mt-2 text-sm text-foreground-500">
                  根据{comic.categories.map((c) => catNameMap[c] || c).join('、')}题材为你推荐
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}